import { ReactChild, useRef, useEffect, useCallback, useState, useMemo } from 'react';
import type { Dispatch } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { Notifier } from 'CorePackage/NotificationModule/components/Notifier';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { getUserRole } from 'AuthPackage/MainModule/selectors/authSelectors';
import { Header } from 'CorePackage/HeaderModule/components/Header';
import { IUserRole } from 'APIPackage/AuthModule/interfaces/IUserRole';
import { UserRoleEnum } from 'APIPackage/AuthModule/enums/UserRoleEnum';
import { newsGetAnnounce } from 'NewsPackage/MainModule/actions/newsActions';
import { getNewsAnnounce } from 'NewsPackage/MainModule/selectors/newsSelectors';
import { catalogGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { INews } from 'APIPackage/NewsModule/interfaces/INews';
import { adminGetFetchSettings } from 'PersonalAccountPackage/AdminModule/actions/adminAction';
import { decodeNewsHtmlText, isNewsHtmlText } from 'NewsPackage/MainModule/common/isNewsHtmlText';
import { rolesDict } from 'NewsPackage/MainModule/constants/rolesDict';

import { coreSetVisibleCheckPluginModal } from '../actions/coreActions';
import { BackToTop } from './BackToTop';
import styles from './Layout.module.scss';
import { CheckPluginModal } from './CheckPluginModal';
import { CheckPluginStatusesEnum } from '../enums/CheckPluginStatusesEnum';
import { useStatusInfoRead } from '../hooks/useStatusInfoRead';
import { useForceUpdate } from '../hooks/useForceUpdate';
import { ROUTE_AUTH, ROUTE_FEEDBACK } from '../routes/routes';
import { getAnnounceLeftTime } from '../common/getAnnounceLeftTime';

const MARGIN_TOP = 152;

interface ILayoutProps {
  withFooter: boolean;
  children: ReactChild | ReactChild[];
  showMenu?: boolean;
  contentCentered?: boolean;
  announce?: INews;
  role: IUserRole | undefined;
  visibleCheckPluginModal: boolean;
  setVisibleCheckPluginModal: (visible: boolean) => void;
}

const LayoutWithoutStore = ({
  withFooter,
  children,
  showMenu = true,
  contentCentered,
  announce,
  role,
  visibleCheckPluginModal,
  setVisibleCheckPluginModal,
}: ILayoutProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const [headerStyle, setHeaderStyle] = useState({ top: '0' });
  const [mainStyle, setMainStyle] = useState({ marginTop: `${MARGIN_TOP}px` });
  const [announceTimeTick, setAnnounceTimeTick] = useState(0);

  useEffect(() => {
    dispatch(catalogGet(CommonCatalogsNamesEnum.ROLES));
    dispatch(newsGetAnnounce());
    dispatch(adminGetFetchSettings());
  }, [dispatch]);

  const contentAttributes = {
    ...(contentCentered && { 'data-centred': true }),
  };

  const announceRef = useRef<HTMLDivElement>(null);
  const forceUpdate = useForceUpdate();
  const isHtmlAnnounce = isNewsHtmlText(announce?.text);

  // Как в newsSaga: без каталога ROLES — иначе анонс пропадает, пока rolesOptions ещё пустой
  const isAnnounceVisibleForRole = useMemo(() => {
    if (!announce?.roles?.length) {
      return false;
    }

    const targetSysName = role?.sysname ?? UserRoleEnum.OPEN_PART;

    return announce.roles.some(roleId => rolesDict[Number(roleId)]?.value === targetSysName);
  }, [announce?.roles, role?.sysname]);

  const announceText = useMemo(() => {
    if (!announce?.text) {
      return '';
    }

    const rawText = announce.countdownStop
      ? announce.text.replace('{time}', getAnnounceLeftTime(announce))
      : announce.text;

    return isNewsHtmlText(rawText) ? decodeNewsHtmlText(rawText) : rawText;
    // tick обновляет {time} в тексте анонса
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announce, announceTimeTick]);

  useEffect(() => {
    if (!announce?.text || !announce.countdownStop) {
      return undefined;
    }

    const interval = setInterval(() => setAnnounceTimeTick(tick => tick + 1), 10000);

    return () => clearInterval(interval);
  }, [announce]);

  useEffect(() => {
    if (announceText) {
      forceUpdate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announceText]);

  const announceHeight = announceRef.current?.offsetHeight;

  useEffect(() => {
    setHeaderStyle({ top: announceHeight ? `${announceHeight}px` : '0' });
    setMainStyle({ marginTop: announceHeight ? `${MARGIN_TOP + announceHeight}px` : `${MARGIN_TOP}px` });
  }, [announceHeight]);

  const { setStatus } = useStatusInfoRead();

  const setNextEntryCheckPlugin = useCallback(() => {
    setStatus({ status: CheckPluginStatusesEnum.AGREE });
    setVisibleCheckPluginModal(false);
  }, [setStatus, setVisibleCheckPluginModal]);

  const setCheckPlugin = useCallback(() => {
    setStatus({ status: CheckPluginStatusesEnum.DISAGREE });
    setVisibleCheckPluginModal(false);
  }, [setStatus, setVisibleCheckPluginModal]);

  const getFeedBackLink = () => {
    if (window.location.pathname.includes(ROUTE_AUTH)) {
      return <a href="https://proverki.gov.ru/portal/public-feedback">форме обратной связи</a>;
    }
    if (window.location.pathname.includes(ROUTE_FEEDBACK)) {
      return <a href={`${ROUTE_FEEDBACK}#feedbackForm`}>форме обратной связи</a>;
    }

    return <Link to={ROUTE_FEEDBACK}>форме обратной связи</Link>;
  };

  return (
    <div className={styles.Wrapper}>
      {announce && announceText && isAnnounceVisibleForRole && (
        <div ref={announceRef} className={isHtmlAnnounce ? styles.HtmlAnnounce : styles.Announce}>
          {isHtmlAnnounce ? (
            // eslint-disable-next-line react/no-danger
            <div dangerouslySetInnerHTML={{ __html: announceText }} />
          ) : (
            <p>{announceText}</p>
          )}
        </div>
      )}
      <Notifier />
      <Header style={headerStyle} showMenu={showMenu} />
      <CheckPluginModal
        setNextEntryCheckPlugin={setNextEntryCheckPlugin}
        setCheckPlugin={setCheckPlugin}
        visible={visibleCheckPluginModal}
      />
      <main className={styles.Content} style={mainStyle} {...contentAttributes}>
        {children}
      </main>
      <BackToTop />
      {withFooter && (
        <footer className={styles.Footer}>
          <div className={styles.FirstContent}>
            Методическая поддержка системы осуществляется Минэкономразвития России. Оператором системы является
            Генеральная прокуратура Российской Федерации.
          </div>
          <div>
            Прием обращений в службу технической поддержки осуществляется по&nbsp;
            {getFeedBackLink()}.
          </div>
        </footer>
      )}
    </div>
  );
};

const mapStateToProps = (state: IRootState) => ({
  announce: getNewsAnnounce(state),
  role: getUserRole(state),
  visibleCheckPluginModal: state.core.visibleCheckPluginModal,
});

const mapDispatchToProps = (dispatch: Dispatch<RootActions>) => ({
  setVisibleCheckPluginModal: (visible: boolean) => dispatch(coreSetVisibleCheckPluginModal(visible)),
});

export const Layout = connect(mapStateToProps, mapDispatchToProps)(LayoutWithoutStore);
